import { Brain } from 'lucide-react';
import { useState } from 'react';

import {
  type MemorySuggestionAnswer,
  buildDismissMemoryReply,
  buildReplaceMemoryReply,
  buildSaveMemoryReply,
} from '../../lib/memory-suggestion-replies';
import { ComposerDecision } from '../composer-gate-context';
import { DecisionFooter } from './decision-footer';

export interface MemorySuggestionConflict {
  knowledgeEntryId: string;
  content: string;
}

export interface MemorySuggestionCardProps {
  /** The tool call this card renders from — a stable id for gate registration. */
  id: string;
  content: string;
  /** Human-readable scope, e.g. "graphics" or "everything Claire creates". */
  scopeLabel: string;
  scopeSkillIds?: string[];
  /**
   * Set when the new content looks like a near-repeat of an EXISTING memory
   * in the same scope (Phase 3 conflict UI,
   * docs/plans/claire-memory-suggestions.md). When present, the card shows
   * the old memory alongside "Replace it" in addition to the usual
   * Save/Dismiss.
   */
  conflictsWith?: MemorySuggestionConflict;
  /**
   * How a later message in the transcript already answered this card (see
   * `getMemorySuggestionAnswers`). Without it a reloaded conversation shows
   * live buttons on a suggestion that was saved, and saving again writes a
   * duplicate memory.
   */
  answer?: MemorySuggestionAnswer;
  /**
   * Cards answer by SENDING A MESSAGE — there is no tool-output channel back
   * from a card. Save sends a follow-up chat message the model turns into a
   * normal `meta_remember` call, carrying the same content and scope. Dismiss
   * sends a decline the model turns into `meta_dismissMemorySuggestion`, so
   * the same suggestion is not offered again.
   */
  onSendMessage: (message: string) => void;
}

/**
 * "Remember this?" — the Save/Dismiss suggestion card for
 * `meta_suggestMemory` (docs/plans/claire-memory-suggestions.md Phase 1).
 *
" * Answered through the same composer decision as every other card
 * (`ComposerDecision` + `DecisionFooter`), so Save / Replace it / Dismiss sit
 * where the owner already answers Claire rather than in bespoke buttons half a
 * transcript up. The decision itself stays lighter than
 * `ConfirmationRequiredCard`'s: reversible, no cost, no persisted token.
 * Visual language borrows from
 * `MemoryRecallBadge` (the Brain icon, the same "Claire remembered" family of
 * copy) rather than introducing a new icon/tone for memory-related UI.
 */
export function MemorySuggestionCard({
  id,
  content,
  scopeLabel,
  scopeSkillIds,
  conflictsWith,
  answer,
  onSendMessage,
}: MemorySuggestionCardProps) {
  const [localAnswer, setLocalAnswer] = useState<MemorySuggestionAnswer>();
  const resolved = answer ?? localAnswer;

  if (resolved === 'dismissed') return null;

  const handleSave = () => {
    setLocalAnswer('saved');
    onSendMessage(buildSaveMemoryReply({ content, scopeSkillIds }));
  };

  const handleReplace = () => {
    if (!conflictsWith) return;
    setLocalAnswer('saved');
    onSendMessage(
      buildReplaceMemoryReply({ content, scopeSkillIds, conflictsWith })
    );
  };

  const handleDismiss = () => {
    setLocalAnswer('dismissed');
    onSendMessage(buildDismissMemoryReply({ content, scopeSkillIds }));
  };

  const composerGate =
    resolved === undefined
      ? {
          sentence: 'Remember this?',
          onAccept: handleSave,
          onReject: handleDismiss,
          acceptLabel: conflictsWith ? 'Keep both' : 'Save',
          rejectLabel: 'Dismiss',
          ...(conflictsWith
            ? {
                additionalActions: [
                  { label: 'Replace it', onClick: handleReplace },
                ],
              }
            : {}),
        }
      : null;

  return (
    <ComposerDecision id={id} gate={composerGate}>
      <div
        data-testid="claire-card-memory-suggestion"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-sm"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <Brain className="size-4 text-muted-foreground" />
          Remember this?
        </div>

        <p className="mt-2 whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
          {content}
        </p>

        <p className="mt-2 text-xs text-muted-foreground">
          Applies to {scopeLabel}.
        </p>

        {conflictsWith && (
          <div className="mt-3 rounded-md border border-dashed p-3">
            <p className="text-xs font-medium text-muted-foreground">
              This looks similar to an existing memory:
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              {conflictsWith.content}
            </p>
          </div>
        )}

        {resolved === 'saved' ? (
          <p className="mt-3 text-xs text-muted-foreground">
            You asked Claire to save this.
          </p>
        ) : (
          <div className="mt-3">
            <DecisionFooter
              hideSentence
              sentence="Remember this?"
              onAccept={handleSave}
              onReject={handleDismiss}
              acceptLabel={conflictsWith ? 'Keep both' : 'Save'}
              rejectLabel="Dismiss"
              {...(conflictsWith
                ? {
                    additionalActions: [
                      { label: 'Replace it', onClick: handleReplace },
                    ],
                  }
                : {})}
            />
          </div>
        )}
      </div>
    </ComposerDecision>
  );
}
